import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CalendarCheck, Target, Users } from 'lucide-react';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const sections = [
  {
    icon: Target,
    title: 'Our Mission',
    body:
      'Our mission is to give every salon, spa, barbershop and beauty business in Africa the tools to run like the best in the world. Bookings, payments and day-to-day management should not require three notebooks, endless WhatsApp threads and a calculator at closing time. We bring it all into one place — so owners spend less time chasing appointments and more time doing the work their clients come back for.',
  },
  {
    icon: CalendarCheck,
    title: 'Our Vision',
    body:
      'We envision a future where booking a braid appointment in Lagos, a massage in Nairobi or a fade in Accra is as effortless as it is anywhere in the world — and where the businesses behind those services grow on their own terms. Spirara aims to be the operating system for Africa\u2019s beauty and wellness economy: the calendar, the till and the client book, working together for millions of independent businesses.',
  },
  {
    icon: Users,
    title: 'Our Values',
    body:
      'We build for the way business is actually done here. That means payments that include mobile money and cash, not just cards. It means software that works on the phone already in your pocket. And it means putting owners and their teams first: their client relationships stay theirs, their data stays theirs, and every feature we ship has to earn its place on a busy shop floor. Trusted by beauty businesses, loved by their clients — that is the standard we hold ourselves to.',
  },
];

const withLocalParam = (path, searchParams) => {
  const local =
    searchParams.get('local') || searchParams.get('locale') || searchParams.get('country');
  if (!local) return path;
  const next = new URLSearchParams();
  next.set('local', local);
  return `${path}?${next.toString()}`;
};

const About = () => {
  const [searchParams] = useSearchParams();

  return (
    <div className="min-h-screen bg-sand-100 font-body text-ink-900">
      <MarketingNav variant="light" current="/about" />

      <main className="mx-auto max-w-4xl px-5 py-16 sm:px-6 sm:py-24 lg:px-8">
        <motion.div {...reveal} className="mb-16 text-center sm:mb-20">
          <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">About us</p>
          <h1 className="mt-4 font-display text-5xl font-light leading-[1.05] tracking-tight text-ink-900 sm:text-6xl">
            About Spirara<span className="text-saffron-500">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink-700/80 sm:text-lg">
            The all-in-one booking, payments and business management platform for Africa&rsquo;s beauty
            and wellness businesses.
          </p>
        </motion.div>

        <div className="space-y-10 sm:space-y-12">
          {sections.map(({ icon: Icon, title, body }) => (
            <motion.section
              key={title}
              {...reveal}
              className="bg-sand-50 p-8 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.35)] ring-1 ring-ink-900/10 sm:p-10"
            >
              <div className="mb-6 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center bg-ink-900">
                  <Icon className="h-6 w-6 text-saffron-400" />
                </div>
                <h2 className="font-display text-2xl font-light text-ink-900 sm:text-3xl">{title}</h2>
              </div>
              <p className="text-base leading-relaxed text-ink-700/85 sm:text-lg">{body}</p>
            </motion.section>
          ))}
        </div>

        <motion.div {...reveal} className="mt-16 text-center sm:mt-20">
          <h2 className="font-display text-3xl font-light leading-tight text-ink-900 sm:text-4xl">
            Start taking bookings this week.
          </h2>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <Link
              to={withLocalParam('/signup', searchParams)}
              className="group inline-flex items-center justify-center gap-2 bg-saffron-500 px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors duration-300 hover:bg-saffron-400"
            >
              Sign up
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to={withLocalParam('/', searchParams)}
              className="inline-flex items-center justify-center px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 ring-1 ring-inset ring-ink-900/25 transition-colors duration-300 hover:bg-ink-900/5"
            >
              Explore Spirara
            </Link>
          </div>
        </motion.div>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default About;
